import React, { useEffect, useMemo, useState } from 'react';
import technicalMaintenanceApi, {
  AUDIT_ACTION_OPTIONS,
  AUDIT_TABLE_OPTIONS,
} from '../../api/technicalMaintenanceApi';
import DataTable from '../../components/ui/DataTable';
import FilterActions from '../../components/ui/FilterActions';
import FilterPanel from '../../components/ui/FilterPanel';
import { Label } from '../../components/ui/Label';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { computeAuditChanges } from './auditChangeUtils';
import './technicalMaintenanceShared.css';

const NARROW_CELL_CLASS = 'whitespace-nowrap max-w-[7.5rem] truncate';
const DETAIL_CELL_CLASS = 'align-top min-w-[16rem] max-w-none break-words whitespace-normal leading-snug';
const CHANGE_CELL_CLASS = 'align-top min-w-[11rem] max-w-none break-words whitespace-normal leading-snug text-gray-800 dark:text-gray-200';

const defaultFilters = {
  search: '',
  user: '',
  table: '',
  action: '',
};

const formatDateTime = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('tr-TR');
};

const parseAuditPayload = (raw) => {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const formatDetail = (log) => {
  const raw = log.action === 'DELETE' ? log.old_value : log.new_value;
  const data = parseAuditPayload(raw);
  if (!data) {
    return raw ? String(raw).slice(0, 240) : '—';
  }

  const noteText = (value) => {
    const text = value != null ? String(value).trim() : '';
    return text || null;
  };

  if (log.table_name === 'equipment') {
    return [
      data.name,
      data.equipment_code,
      data.area,
      noteText(data.notes),
    ].filter(Boolean).join(' · ') || '—';
  }
  if (log.table_name === 'maintenance_records') {
    return [
      data.title,
      data.equipment_name,
      data.performed_by,
      noteText(data.description),
    ].filter(Boolean).join(' · ') || '—';
  }
  if (log.table_name === 'maintenance_invoices') {
    return [
      data.invoice_no,
      data.supplier,
      data.total_amount != null ? `${data.total_amount} ${data.currency || 'TRY'}` : '',
      noteText(data.notes),
    ].filter(Boolean).join(' · ') || '—';
  }

  return JSON.stringify(data).slice(0, 240);
};

const findPreviousSnapshot = (log, allRows) => {
  if (!Array.isArray(allRows) || !allRows.length) return null;
  const logTime = new Date(log.created_at).getTime();
  const previous = allRows
    .filter((row) =>
      row.id !== log.id
      && row.record_id === log.record_id
      && row.table_name === log.table_name
      && new Date(row.created_at).getTime() < logTime)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
  if (!previous) return null;
  if (previous.action === 'DELETE') return previous.old_value;
  return previous.new_value;
};

const formatChanges = (log, allRows = []) => {
  let items = Array.isArray(log.changes) ? log.changes.filter(Boolean) : [];
  if (!items.length && log.action === 'UPDATE') {
    items = computeAuditChanges(log.table_name, log.action, log.old_value, log.new_value);
  }
  if (!items.length && log.action === 'UPDATE') {
    const previousSnapshot = findPreviousSnapshot(log, allRows);
    if (previousSnapshot) {
      items = computeAuditChanges(log.table_name, 'UPDATE', previousSnapshot, log.new_value);
    }
  }
  if (!items.length) {
    if (log.action === 'INSERT') return '—';
    if (log.action === 'DELETE') return 'Kayıt silindi';
    return '—';
  }
  return items;
};

const normalizeSearch = (value) => String(value ?? '').toLocaleLowerCase('tr');

const getActivitySearchText = (log, allRows = []) => {
  const changes = formatChanges(log, allRows);
  const changeText = Array.isArray(changes) ? changes.join(' ') : (changes || '');
  return normalizeSearch([
    log.full_name,
    log.username,
    log.table_label,
    log.table_name,
    log.action_label,
    log.action,
    log.record_id,
    formatDetail(log),
    changeText,
    log.old_value,
    log.new_value,
  ].filter((part) => part != null && part !== '' && part !== '—').join(' '));
};

const matchesActivityFilters = (row, activeFilters, allRows) => {
  if (activeFilters.user && String(row.user) !== String(activeFilters.user)) {
    return false;
  }
  if (activeFilters.table && row.table_name !== activeFilters.table) {
    return false;
  }
  if (activeFilters.action && row.action !== activeFilters.action) {
    return false;
  }
  const searchTerm = normalizeSearch(activeFilters.search.trim());
  if (searchTerm && !getActivitySearchText(row, allRows).includes(searchTerm)) {
    return false;
  }
  return true;
};

const TechnicalMaintenanceActivityPage = () => {
  const [allRows, setAllRows] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [filters, setFilters] = useState(defaultFilters);
  const [draftFilters, setDraftFilters] = useState(defaultFilters);
  const [userOptions, setUserOptions] = useState([]);

  const buildUserOptions = (data) => {
    const map = new Map();
    data.forEach((row) => {
      if (!row.user) return;
      if (!map.has(row.user)) {
        map.set(row.user, {
          value: String(row.user),
          label: row.full_name && row.full_name !== '-' ? row.full_name : row.username,
        });
      }
    });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label, 'tr'));
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await technicalMaintenanceApi.getAuditLogs({ limit: 500 });
      const data = response?.data || [];
      setAllRows(data);
      setUserOptions(buildUserOptions(data));
    } catch (err) {
      setError(err.message || 'İşlem geçmişi yüklenemedi.');
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    setRows(allRows.filter((row) => matchesActivityFilters(row, filters, allRows)));
  }, [allRows, filters]);

  const applyFilters = () => {
    setError(null);
    setFilters({ ...draftFilters });
  };

  const resetFilters = () => {
    setDraftFilters(defaultFilters);
    setFilters(defaultFilters);
  };

  const columns = useMemo(() => [
    {
      header: 'Tarih',
      accessorKey: 'created_at',
      cell: ({ getValue }) => formatDateTime(getValue()),
      size: 158,
      maxSize: 158,
    },
    {
      header: 'Kullanıcı',
      accessorKey: 'full_name',
      size: 108,
      maxSize: 116,
      headerClassName: 'whitespace-nowrap',
      cellClassName: NARROW_CELL_CLASS,
      cell: ({ row }) => {
        const name = row.original.full_name;
        return name && name !== '-' ? name : row.original.username || '—';
      },
    },
    {
      header: 'Menü',
      accessorKey: 'table_label',
      size: 118,
      maxSize: 128,
      headerClassName: 'whitespace-nowrap',
      cellClassName: NARROW_CELL_CLASS,
    },
    {
      header: 'İşlem',
      accessorKey: 'action_label',
      size: 100,
      maxSize: 108,
      headerClassName: 'whitespace-nowrap',
      cellClassName: 'whitespace-nowrap',
    },
    {
      header: 'Kayıt #',
      accessorKey: 'record_id',
      size: 72,
      maxSize: 80,
      headerClassName: 'whitespace-nowrap',
      cellClassName: 'whitespace-nowrap tabular-nums',
    },
    {
      id: 'detail',
      header: 'Özet',
      size: 340,
      minSize: 260,
      headerClassName: 'align-top',
      cellClassName: DETAIL_CELL_CLASS,
      cell: ({ row }) => formatDetail(row.original),
    },
    {
      id: 'changes',
      header: 'Değişiklik',
      size: 240,
      minSize: 180,
      headerClassName: 'align-top',
      cellClassName: CHANGE_CELL_CLASS,
      cell: ({ row }) => {
        const changes = formatChanges(row.original, allRows);
        if (changes === '—' || changes === 'Kayıt silindi') {
          return changes;
        }
        return (
          <ul className="list-none space-y-0.5 m-0 p-0">
            {changes.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        );
      },
    },
  ], [allRows]);

  return (
    <div className="tm-page space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">İşlem Geçmişi</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Teknik bakım modülünde yapılan ekipman, bakım kaydı ve fatura değişiklikleri.
        </p>
      </div>

      <FilterPanel isOpen={isFilterOpen} onToggle={() => setIsFilterOpen((v) => !v)}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <div>
            <Label>Ara</Label>
            <Input
              value={draftFilters.search}
              onChange={(e) => setDraftFilters({ ...draftFilters, search: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
              placeholder="Özet, kullanıcı, kayıt no, menü..."
            />
          </div>
          <div>
            <Label>Kullanıcı</Label>
            <Select
              value={draftFilters.user}
              onChange={(e) => setDraftFilters({ ...draftFilters, user: e.target.value })}
            >
              <option value="">Tüm kullanıcılar</option>
              {userOptions.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </Select>
          </div>
          <div>
            <Label>Menü</Label>
            <Select
              value={draftFilters.table}
              onChange={(e) => setDraftFilters({ ...draftFilters, table: e.target.value })}
            >
              {AUDIT_TABLE_OPTIONS.map((item) => (
                <option key={item.value || 'all'} value={item.value}>{item.label}</option>
              ))}
            </Select>
          </div>
          <div>
            <Label>İşlem</Label>
            <Select
              value={draftFilters.action}
              onChange={(e) => setDraftFilters({ ...draftFilters, action: e.target.value })}
            >
              {AUDIT_ACTION_OPTIONS.map((item) => (
                <option key={item.value || 'all'} value={item.value}>{item.label}</option>
              ))}
            </Select>
          </div>
        </div>
        <FilterActions onSearch={applyFilters} onClear={resetFilters} />
      </FilterPanel>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <DataTable
        columns={columns}
        data={rows}
        isLoading={loading}
        showSearch={false}
        fixedTableLayout
        fixedHorizontalScroll
        emptyMessage={filters.search || filters.user || filters.table || filters.action
          ? 'Arama kriterlerine uygun işlem kaydı bulunamadı.'
          : 'Henüz işlem kaydı yok. Yeni ekipman, bakım veya fatura işlemleri burada görünecek.'}
      />
    </div>
  );
};

export default TechnicalMaintenanceActivityPage;
